import { debounce } from "lodash";
import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../store";
import { actions } from "../actions";
import { selectors } from "../selectors";
import { MenuItem } from "store/types";
import { hasClosedChartStorageKey } from "ui/components/panels/graphs/constants";
var FULL_CALC_WITHOUT_ES_DEBOUNCE_TIME = 2000;
var FULL_CALC_WITH_ES_DEBOUNCE_TIME = 2000;
export var CalculationObservers = function () {
    var dispatch = useAppDispatch();
    var preloadedPerformanceData = useAppSelector(selectors.selectPreloadedPerformance);
    var isSystemPerformanceLoading = useAppSelector(selectors.selectIsPerformanceLoading);
    var performanceError = useAppSelector(selectors.selectPerformanceError);
    //Shows preloaded performance data (or surfaces an error) once the api call
    //settles, if performance data is currently loading.
    useEffect(function () {
        if (isSystemPerformanceLoading) {
            dispatch(actions.showPerformanceData());
        }
    }, [
        dispatch,
        isSystemPerformanceLoading,
        preloadedPerformanceData,
        performanceError,
    ]);
    useTriggerSanityCheck();
    useTriggerCalculationWithEs();
    return null;
};
//Trigger a debounced System Performance data with ES request when system performance request without ES is completed.
function useTriggerCalculationWithEs() {
    var dispatch = useAppDispatch();
    var isSystemPerformanceDataPending = useAppSelector(selectors.selectIsPerformanceDataPending);
    var preloadedPerformance = useAppSelector(selectors.selectPreloadedPerformance);
    var debouncedSystemPerformanceWithEs = useMemo(function () {
        return debounce(function () {
            return dispatch(actions.getSystemPerformanceWithEs());
        }, FULL_CALC_WITH_ES_DEBOUNCE_TIME);
    }, [dispatch]);
    useEffect(function () {
        if (isSystemPerformanceDataPending ||
            !(preloadedPerformance === null || preloadedPerformance === void 0 ? void 0 : preloadedPerformance.energySavingOff)) {
            return;
        }
        var promise = debouncedSystemPerformanceWithEs();
        return function () {
            promise === null || promise === void 0 ? void 0 : promise.abort();
        };
    }, [
        debouncedSystemPerformanceWithEs,
        dispatch,
        isSystemPerformanceDataPending,
        preloadedPerformance === null || preloadedPerformance === void 0 ? void 0 : preloadedPerformance.energySavingOff,
    ]);
}
function useTriggerSanityCheck() {
    var dispatch = useAppDispatch();
    var nodes = useAppSelector(selectors.selectNodes);
    var edges = useAppSelector(selectors.selectEdges);
    var attachments = useAppSelector(selectors.selectAttachments);
    var isPerformanceDataValid = useAppSelector(selectors.selectIsPerformanceDataValid);
    var hasReachedPartSecured = useAppSelector(selectors.selectHasReachedPartSecured);
    var manualCalculationInput = useAppSelector(selectors.selectManualCalculationInput);
    var systemDataDefineParameters = useAppSelector(selectors.selectSystemDataDefineParameters);
    var debouncedSanityCheck = useMemo(function () {
        return debounce(function () {
            var promise = dispatch(actions.getSystemPerformance());
            promise.then(function (result) {
                if (result.meta.requestStatus === "rejected" && result.meta.aborted) {
                    return;
                }
                dispatch(actions.setHasPendingSanityCheck(false));
            });
            return promise;
        }, FULL_CALC_WITHOUT_ES_DEBOUNCE_TIME);
    }, [dispatch]);
    useEffect(function () {
        if (!systemDataDefineParameters ||
            Object.keys(systemDataDefineParameters.parameters).length === 0)
            return;
        dispatch(actions.markCalculationPending());
        dispatch(actions.setHasPendingSanityCheck(true));
        var promise = debouncedSanityCheck();
        return function () {
            promise === null || promise === void 0 ? void 0 : promise.abort();
        };
    }, [
        nodes,
        edges,
        attachments,
        systemDataDefineParameters,
        manualCalculationInput,
        dispatch,
        debouncedSanityCheck,
    ]);
    useEffect(function () {
        var userHasClosedChart = Boolean(sessionStorage.getItem(hasClosedChartStorageKey));
        // Only auto-open the graph panel once a calculation has produced data the
        // graphs can actually show. When part secured was never reached the data
        // is suppressed (see response-deserializer), so opening the panel would
        // just present an empty/broken result.
        if (!isPerformanceDataValid ||
            !hasReachedPartSecured ||
            userHasClosedChart) {
            return;
        }
        dispatch(actions.toggleGraphPanel(true));
        dispatch(actions.openMenuItem(MenuItem.APPLICATION_SPEED));
    }, [dispatch, isPerformanceDataValid, hasReachedPartSecured]);
}
